import React, { useState } from 'react';
import { getLatestReview } from '../../utils/reviewUtils';
import { LOCATION_ID_TO_NAME } from '../../utils/constants';
import useUserStore from "../../stores/userStore";
import { hasPermission } from "../../utils/permissions";
import LoadingSpinner from "../../LoadingSpinner";
import {fetchUserEventList} from "../../services/eventService";
import useModalStore from "../../stores/modalStore";
import { Check, X, Trash2, Settings } from "lucide-react";
import EventDetailModal from "../modals/EventDetailModal";
import useSettingsStore from "../../stores/settingsStore";

const eventTypeBadge = (type) => {
    const base = "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap";
    switch (type) {
        case "clock in": return `${base} bg-emerald-100 text-emerald-700`;
        case "clock out": return `${base} bg-rose-100 text-rose-700`;
        case "sick": return `${base} bg-purple-100 text-purple-700`;
        case "time off": return `${base} bg-blue-100 text-blue-700`;
        case "note": return `${base} bg-amber-100 text-amber-700`;
        case "comped service": return `${base} bg-teal-100 text-teal-700`;
        default: return `${base} bg-gray-100 text-gray-600`;
    }
};

const statusBadge = (isReviewed, decision) => {
    if (!isReviewed) return "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap";
    if (decision) return "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 whitespace-nowrap";
    return "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 whitespace-nowrap";
};

const formatTs = (ts, mode) => {
    if (!ts) return '—';
    return mode === 'epoch' ? new Date(ts).getTime() : new Date(ts).toLocaleString();
};

const ToDoListRow = ({ event, onDecision, onDelete, deleting = false, timestampModes, selected = false, onToggleSelect }) => {
    const { openModal } = useModalStore();
    const { userIdToFirstName, setEmployeeEvents } = useUserStore();
    const piiClass = useSettingsStore(s => s.blurPII) ? 'blur-pii' : '';
    const [detailEvent, setDetailEvent] = useState(null);
    const latestReview = getLatestReview(event.reviews);
    const isReviewed = !!latestReview;

    return (
        <tr className={`border-b border-gray-50 transition-colors ${isReviewed ? 'opacity-50' : ''} ${selected ? '!bg-indigo-50/60' : 'hover:bg-indigo-50/30'}`}>
            {onToggleSelect && (
                <td className="px-4 py-2">
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => onToggleSelect(event.id)}
                        className="accent-indigo-600 cursor-pointer rounded"
                    />
                </td>
            )}
            <td
                className={`px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 cursor-pointer text-left ${piiClass}`}
                onClick={async () => {
                    openModal("eventListModal", {userId: event.user_id});
                    await fetchUserEventList(event.user_id, setEmployeeEvents, "todolistrow name click");
                }}
            >
                {userIdToFirstName[event.user_id] || event.user_id}
            </td>

            <td className="px-4 py-2 text-left">
                <span className={eventTypeBadge(event.event_type)}>{event.event_type}</span>
            </td>
            <td className="px-4 py-2 text-xs text-gray-500 hidden sm:table-cell text-left">
                {LOCATION_ID_TO_NAME[event.location_id]}
            </td>

            <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap text-left">
                {formatTs(event.insert_timestamp, timestampModes?.insert)}
            </td>

            <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap text-left">
                {formatTs(event.event_timestamp, timestampModes?.event)}
            </td>

            <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap text-left">
                {formatTs(event.event_end_timestamp, timestampModes?.end)}
            </td>

            <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap text-left">
                {formatTs(latestReview?.decision_timestamp, timestampModes?.reviewedAt)}
            </td>

            <td
                className="px-4 py-2 text-xs text-gray-500 max-w-[180px] truncate text-left"
                title={event.notes || ''}
            >
                {event.notes || '—'}
            </td>

            <td className="px-4 py-2 text-xs text-gray-600 text-left">{event.value || '—'}</td>

            <td className="px-4 py-2 text-left">
                <span className={statusBadge(isReviewed, latestReview?.decision)}>
                    {isReviewed ? (latestReview.decision ? 'Approved' : 'Denied') : 'Pending'}
                </span>
            </td>

            <td className={`px-4 py-2 text-xs text-gray-500 text-left ${piiClass}`}>
                {isReviewed ? (userIdToFirstName[latestReview.reviewer_id] || latestReview.reviewer_id) : '—'}
            </td>
            <td className="px-4 py-2 text-xs text-gray-500 text-left">{latestReview?.note || '—'}</td>

            {hasPermission('approve_deny_events') && (
                <td className="px-4 py-2">
                    <div className="flex items-center justify-center gap-1">
                        <button
                            onClick={() => setDetailEvent(event)}
                            className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 hover:!border-gray-300 hover:!text-gray-600 !text-gray-400 !transition-colors"
                            title="View details"
                        >
                            <Settings size={13} />
                        </button>
                        <button
                            onClick={() => onDecision(event.id, 'approve')}
                            disabled={isReviewed}
                            className={`!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-emerald-50 hover:!border-emerald-200 hover:!text-emerald-600 !text-gray-400 !transition-colors ${isReviewed ? '!opacity-30 !pointer-events-none' : ''}`}
                            title="Approve"
                        >
                            <Check size={13} />
                        </button>
                        <button
                            onClick={() => onDecision(event.id, 'deny')}
                            disabled={isReviewed}
                            className={`!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-rose-50 hover:!border-rose-200 hover:!text-rose-600 !text-gray-400 !transition-colors ${isReviewed ? '!opacity-30 !pointer-events-none' : ''}`}
                            title="Deny"
                        >
                            <X size={13} />
                        </button>
                        {onDelete && (
                            deleting ? (
                                <div className="p-1.5"><LoadingSpinner small /></div>
                            ) : (
                                <button
                                    onClick={onDelete}
                                    disabled={deleting}
                                    className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-red-50 hover:!border-red-200 hover:!text-red-600 !text-gray-400 !transition-colors"
                                    title="Delete"
                                >
                                    <Trash2 size={13} />
                                </button>
                            )
                        )}
                    </div>
                </td>
            )}
            {detailEvent && (
                <EventDetailModal
                    event={detailEvent}
                    userIdToFirstName={userIdToFirstName}
                    onClose={() => setDetailEvent(null)}
                />
            )}
        </tr>
    );
};

export default React.memo(ToDoListRow);
